import React, {useEffect, useMemo} from 'react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import {
	LinearProgress,
	ListItem,
	ListItemAvatar,
	ListItemSecondaryAction,
	ListItemText,
	Tooltip,
} from '@mui/material';
import {ActionMenuIconButton} from '@netx/core-components/ActionMenuIconButton';
import {GenericImageAvatar} from '@netx/core-components/GenericImageAvatar';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {bytes} from '@netx/core/lib/underscore-netx';
import {getGenericIconUrl} from '@netx/core/utils/getGenericIconUrl';
import {uploadBuilderActionRemoveFile} from '../../actions';
import type {AnyUppyFile} from '../../types';
import {
	getUploadSingleFileItemUtilityClass,
	UPLOAD_SINGLE_FILE_ITEM_CLASS_NAME,
	type UploadSingleFileItemClasses,
	uploadSingleFileItemClasses,
} from './uploadSingleFileItemClasses';

type UploadSingleFileItemOwnerState = WithClasses<UploadSingleFileItemClasses>;

const useUtilityClasses = (ownerState: UploadSingleFileItemOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getUploadSingleFileItemUtilityClass, classes);
};

const UploadSingleFileItemRoot = styled('div', {
	name: UPLOAD_SINGLE_FILE_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	border: `1px solid ${theme.palette.custom.border}`,
	borderRadius: theme.shape.borderRadius,
	position: 'relative',
	'&.disabled': {
		...theme.mixins.disabled,
	},
}));

const UploadSingleFileItemProgress = styled(LinearProgress)(({theme}) => ({
	margin: `-6px 48px 10px 72px`,
}));

export interface UploadSingleFileItemProps
	extends WithClasses<UploadSingleFileItemClasses> {
	disabled?: boolean;
	error?: boolean;
	file: AnyUppyFile;
	percentage?: number;
	uploadComplete?: boolean;
	uploadStarted?: boolean;
	onCancelPreview?: () => void;
	onRemove?: () => void;
	onRequestPreview?: () => void;
}

export const UploadSingleFileItemPrimitive = ({
	classes: _classes,
	className,
	disabled,
	error,
	file,
	percentage = 0,
	uploadComplete,
	uploadStarted,
	onCancelPreview,
	onRemove,
	onRequestPreview,
	...props
}: UploadSingleFileItemProps) => {
	const ownerState = {...props, classes: _classes, className};

	const classes = useUtilityClasses(ownerState);

	const actions = useMemo(
		() =>
			[
				uploadBuilderActionRemoveFile(file, {
					cb: (file) => onRemove?.(),
					enableTest: () => onRemove != null,
				}),
			].filter((action) => action.enableTest?.() !== false),
		[onRemove],
	);

	useEffect(() => {
		onRequestPreview?.();
		return () => {
			onCancelPreview?.();
		};
	}, [file]);

	return (
		<UploadSingleFileItemRoot
			className={clsx(classes.root, className)}
			{...props}
		>
			<ListItem>
				<ListItemAvatar>
					<GenericImageAvatar
						alt={'file preview'}
						src={file.preview || getGenericIconUrl(file.extension, file.name)}
					/>
				</ListItemAvatar>

				<ListItemText
					primaryTypographyProps={{noWrap: true}}
					primary={file.name}
					secondary={`${bytes(file.size)}`}
				/>

				{!!actions.length && (
					<ListItemSecondaryAction>
						<ActionMenuIconButton
							actions={actions}
							IconButtonProps={{
								'data-testid': 'remove-item',
								disabled:
									disabled || (!error && uploadStarted && !uploadComplete),
								edge: 'end',
							}}
						/>
					</ListItemSecondaryAction>
				)}
			</ListItem>

			{uploadStarted && (
				<Tooltip
					disableInteractive
					title={uploadStarted ? `${percentage}%` : undefined}
					placement="bottom-end"
				>
					<UploadSingleFileItemProgress
						variant={'determinate'}
						value={percentage}
						sx={{
							// visibility: uploadStarted ? 'visible' : 'hidden',
							opacity: error ? 0.25 : undefined,
						}}
					/>
				</Tooltip>
			)}
		</UploadSingleFileItemRoot>
	);
};

export const UploadSingleFileItem = observer(UploadSingleFileItemPrimitive);
